<script lang="ts">
	import type { BlogArticle } from '$models/Blog/BlogArticle';
	import SliderWrapper from '$components/UI/Slider/SliderWrapper.svelte';
	import { sanitizeHtml } from '$lib/utils';

	interface Props {
		title: string;
		items: BlogArticle[];
	}

	const { title, items }: Props = $props();
</script>

{#if items.length > 0}
	<div class="related-blogs">
		<div class="related-blogs-header">
			<h2 class="related-blogs-title">{title}</h2>
		</div>

		<div class="related-blogs-slider">
			<SliderWrapper options={{ loop: false, align: 'start', inViewThreshold: 1 }}>
				{#snippet slides(Slide)}
					{#each items as item}
						<Slide>
							<a href={item.button.url} class="related-blogs-item has-link" aria-label={item.title}>
								<div class="related-blogs-item-link"></div>
								<div class="related-blogs-item-arrow"></div>
								<div class="related-blogs-item-content">
									<div class="related-blogs-item-info">
										{#if item.intro}
											<div class="related-blogs-item-text">
												{@html sanitizeHtml(item.intro)}
											</div>
										{/if}
										<h3 class="related-blogs-item-title">
											{@html item.title}
										</h3>
									</div>
								</div>
								{#if item.featuredImagePortrait}
									<figure class="related-blogs-item-image">
										<img
											src={item.featuredImagePortrait.url}
											width={item.featuredImagePortrait.width}
											height={item.featuredImagePortrait.height}
											alt={item.featuredImagePortrait.alt || ''}
											loading="lazy"
											decoding="async"
										/>
									</figure>
								{/if}
							</a>
						</Slide>
					{/each}
				{/snippet}
			</SliderWrapper>
		</div>
	</div>
{/if}

<style lang="postcss" global>
	.related-blogs {
		&-header {
			&:not(:last-child) {
				margin-bottom: var(--spacing-6);
			}
		}

		&-title {
			line-height: 1;
			font-size: 28px;
		}

		&-slider {
			position: relative;
		}

		.slider,
		.slider-viewport {
			overflow: visible;
		}

		.slider {
			--slide-size: 300px;
		}

		@media (--lg) {
			&-title {
				font-size: 40px;
			}
		}

		@media (--extra-large) {
			.slider {
				--slide-size: 25%;
			}
		}
	}

	.related-blogs-item {
		& {
			display: block;
			position: relative;
			border-radius: var(--radius-5);
			overflow: hidden;
			aspect-ratio: 5 / 7;
			background-color: var(--color-secondary);
		}

		&-image {
			height: 100%;
			transition: transform var(--transition);

			img {
				display: block;
				width: 100%;
				height: 100%;
				object-fit: cover;
				transition: background-color var(--transition);
			}
		}

		&-content {
			position: absolute;
			inset: 16px;
			display: flex;
			flex-direction: column;
			align-self: flex-end;
			z-index: 1;
		}

		&-info {
			display: flex;
			flex-direction: column;
			gap: 4px;
			padding: 16px;
			border-radius: 8px;
			background-color: rgba(255, 255, 255, 0.8);
		}

		&-text {
			font-family: var(--font-primary);
			font-size: 14px;
			display: -webkit-box;
			-webkit-line-clamp: 6;
			-webkit-box-orient: vertical;
			overflow: hidden;
			order: 2;
		}

		&-title {
			font-size: 18px;
			line-height: 1;
		}

		&-link {
			&:before {
				content: '';
				position: absolute;
				inset: 0;
				z-index: 2;
				cursor: pointer;
			}
		}

		&-link:hover + &-arrow {
			background-color: var(--color-primary-dark);
		}

		&.has-link {
			cursor: pointer;
		}

		&.has-link:hover &-image {
			transform: scale(1.05);
		}

		@media (--lg) {
			&-text {
				font-size: 16px;
			}

			&-title {
				font-size: 18px;
			}
		}
	}
</style>
